import { forwardRef, type SelectHTMLAttributes, type ReactNode } from "react";
import clsx from "clsx";

/**
 * 選択肢の型
 */
export interface SelectOption {
    /** 値 */
    value: string;
    /** 表示ラベル */
    label: string;
    /** 左側アイコン */
    icon?: ReactNode;
    /** 無効 */
    disabled?: boolean;
}

/**
 * SelectBox コンポーネントのProps
 */
export interface SelectBoxProps
    extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
    /** ラベルテキスト */
    label?: string;
    /** 選択肢 */
    options: SelectOption[];
    /** プレースホルダー（デフォルト値なしの場合） */
    placeholder?: string;
    /** デフォルト値を表示するか */
    showDefaultOption?: boolean;
    /** エラーメッセージ */
    error?: string;
    /** ヘルプテキスト */
    helpText?: string;
    /** サイズ */
    size?: "sm" | "md" | "lg";
    /** フルwidth */
    fullWidth?: boolean;
    /** 無効状態 */
    disabled?: boolean;
}

/**
 * SelectBox - セレクトボックスコンポーネント
 *
 * @description
 * デフォルト値あり/なし、アイコン付き選択肢をサポート
 */
export const SelectBox = forwardRef<HTMLSelectElement, SelectBoxProps>(
    (
        {
            label,
            options,
            placeholder = "選択してください",
            showDefaultOption = true,
            error,
            helpText,
            size = "md",
            fullWidth = true,
            disabled = false,
            className,
            ...props
        },
        ref
    ) => {
        // サイズに応じたクラス
        const sizeClasses = {
            sm: "px-3 py-2 text-sm",
            md: "px-4 py-3 text-base",
            lg: "px-4 py-4 text-lg",
        };

        return (
            <div className={clsx("flex flex-col gap-1", fullWidth && "w-full")}>
                {/* ラベル */}
                {label && (
                    <label className="text-sm font-medium text-gray-700">
                        {label}
                    </label>
                )}

                {/* セレクトボックス */}
                <div className="relative">
                    <select
                        ref={ref}
                        disabled={disabled}
                        className={clsx(
                            "w-full appearance-none rounded-lg border bg-white",
                            "transition-colors duration-200",
                            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
                            "pr-10", // 矢印アイコン用のスペース
                            sizeClasses[size],
                            error
                                ? "border-error-500 focus:ring-error-500 focus:border-error-500"
                                : "border-gray-300 hover:border-gray-400",
                            disabled && "bg-gray-100 cursor-not-allowed opacity-60",
                            className
                        )}
                        {...props}
                    >
                        {/* デフォルトオプション（プレースホルダー） */}
                        {showDefaultOption && (
                            <option value="" disabled>
                                {placeholder}
                            </option>
                        )}

                        {/* 選択肢 */}
                        {options.map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>

                    {/* 矢印アイコン */}
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                        <ChevronDownIcon className="w-5 h-5" />
                    </div>
                </div>

                {/* エラーメッセージ */}
                {error && <span className="text-sm text-error-500">{error}</span>}

                {/* ヘルプテキスト */}
                {helpText && !error && (
                    <span className="text-sm text-gray-500">{helpText}</span>
                )}
            </div>
        );
    }
);

SelectBox.displayName = "SelectBox";

// 矢印アイコン
const ChevronDownIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
    </svg>
);

export default SelectBox;
